import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SectionLabel from '../components/SectionLabel';
import { Newspaper, ChevronDown, Clock, Loader2 } from 'lucide-react';
import { fetchInsights } from '../lib/api';

const InsightCard = ({ post, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.6, delay: (i % 2) * 0.08 }}
      className={`group rounded-xl border bg-[#0f1a2f]/50 transition-all ${open ? 'border-orange-400/40 shadow-[0_12px_40px_-12px_rgba(251,146,60,0.2)]' : 'border-white/8 hover:border-orange-400/40'}`}
    >
      <button type="button" onClick={() => setOpen(s => !s)}
        className="w-full text-left p-7 focus:outline-none" aria-expanded={open}>
        <div className="flex items-center justify-between text-[11px] font-mono tracking-[0.18em] text-slate-500">
          <span className="text-orange-300">{(post.category || '').toUpperCase()}</span>
          <span>{post.date}</span>
        </div>
        <h2 className={`mt-4 font-serif text-2xl leading-snug transition-colors ${open ? 'text-orange-200' : 'text-white'}`}>{post.title}</h2>
        <p className="mt-3 text-sm text-slate-400 leading-relaxed">{post.excerpt}</p>
        <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-between">
          <div className="inline-flex items-center gap-1.5 text-xs text-slate-500 font-mono"><Clock className="w-3.5 h-3.5" /> {post.read_time}</div>
          <span className="inline-flex items-center gap-1.5 text-sm text-orange-300 font-medium">
            {open ? 'Hide article' : 'Read article'}
            <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }}>
              <ChevronDown className="w-4 h-4" />
            </motion.span>
          </span>
        </div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="content" initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden">
            <div className="px-7 pb-7">
              <div className="h-px bg-gradient-to-r from-transparent via-orange-400/30 to-transparent mb-5" />
              <div className="prose prose-invert prose-sm max-w-none prose-headings:font-serif prose-headings:text-white prose-h2:text-xl prose-h2:mt-4 prose-p:text-slate-300 prose-strong:text-orange-200 prose-a:text-orange-300 prose-li:text-slate-300">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content || ''}</ReactMarkdown>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
};

const Insights = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchInsights()
      .then(data => { setPosts(data); setLoading(false); })
      .catch(err => { setError(err?.message || 'Failed to load insights'); setLoading(false); });
  }, []);

  return (
    <div>
      <section className="pt-20 pb-10 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-[1320px] mx-auto px-6">
          <SectionLabel icon={Newspaper}>INSIGHTS &amp; ANALYSIS</SectionLabel>
          <h1 className="mt-4 font-serif text-white text-5xl md:text-6xl leading-[1.05] tracking-tight max-w-4xl">
            Practical field commentary on road asset governance.
          </h1>
          <p className="mt-5 max-w-2xl text-slate-400 text-lg leading-relaxed">
            Short-form articles distilled from live advisory engagements &mdash; focused on decisions state road authorities and Tier-1 consultants actually face.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-[1320px] mx-auto px-6">
          {loading && (
            <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading insights…</div>
          )}
          {error && <div className="text-sm text-red-400">{error}</div>}
          {!loading && !error && posts.length === 0 && (
            <div className="text-slate-400 text-sm">No insights published yet.</div>
          )}
          <div className="grid md:grid-cols-2 gap-6">
            {posts.map((post, i) => <InsightCard key={post.id} post={post} i={i} />)}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Insights;
